//Built-in libraries

//Third party libraries
import {
  CalendarIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  DocumentIcon,
  IdentificationIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { postApplication } from '@/api/Rover/ApplicationForm';
import type { IGetApplicationFormInfoServiceRes, IPostApplicationServicePayload } from '@/api/Rover/ApplicationForm/types';
import SubmitSuccess from '@/components/Applications/SubmitSuccess';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
//Custom components
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
//Services
import { ApplicationFormProvider, useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import DefaultApplicantInformation from '../../Components/DefaultApplicantInformation';
import Application from './ApplicationInformation';
//Assets and Miscellaneous
import { initialValues } from './constants';
import Documents from './Documents';
import HouseholdIncome from './HouseholdIncome';
import LandingPage from './LandingPage';
import Review from './Review';
import Scope from './Scope';

//STP Libraries

import { CRValidationSchema } from './validationSchema';

//Component Begin

interface ICRApplicationFormProps {
  formData: IGetApplicationFormInfoServiceRes;
}

const ApplicationFormBase = ({ formData }: ICRApplicationFormProps) => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const { currentStep, setCurrentStep, updateData, data } = useApplicationFormContext();

  const steps = [
    {
      name: 'Applicant Information',
      sectionId: 'applicantInformation',
      Icon: IdentificationIcon,
      component: <DefaultApplicantInformation />,
    },
    {
      name: 'Application',
      sectionId: 'application',
      Icon: ClipboardDocumentListIcon,
      component: <Application />,
    },
    {
      name: 'Household Income',
      sectionId: 'householdIncome',
      Icon: UserGroupIcon,
      component: <HouseholdIncome />,
    },
    {
      name: 'Documents',
      sectionId: 'documents',
      Icon: DocumentIcon,
      component: <Documents />,
    },
    {
      name: 'Scope',
      sectionId: 'scope',
      Icon: CalendarIcon,
      component: <Scope />,
    },
    {
      name: 'Review',
      sectionId: 'review',
      Icon: ClipboardDocumentCheckIcon,
      component: <Review />,
    },
  ];

  const isLast = currentStep === steps.length - 1;

  const submitApplication = useMutation((data: IPostApplicationServicePayload) => postApplication({ data }), {
    onSuccess: () => {
      setIsSubmitSuccess(true);
    },
  });

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      updateData({
        [steps[currentStep].sectionId]: values[steps[currentStep].sectionId],
      });
      setCurrentStep((prev) => prev + 1);
    } else {
      const applicationData: IPostApplicationServicePayload = {
        formId: formData?.data?.id,
        contactName: data.applicantInformation.fullName,
        contactEmail: data.applicantInformation.contactEmail,
        applicationData: data,
        files: [
          ...(data?.documents?.proofOfOwnership || []),
          ...(data?.documents?.proofOfOccupancy || []),
          ...(data?.documents?.zeroIncomeAffidavit || []),
          ...(data?.documents?.bankStatements || []),
          ...(data?.documents?.proofOfIncome || []),
        ],
      };
      submitApplication.mutate(applicationData);
    }
  };

  if (isSubmitSuccess) {
    return <SubmitSuccess message={formData.data.confirmationMessage ?? `Thank you for your interest in ${formData.data.name}.`} />;
  }

  // Show landing page if terms haven't been accepted
  if (!hasAcceptedTerms) {
    return <LandingPage onAccept={() => setHasAcceptedTerms(true)} formName={formData?.data?.name} />;
  }

  return (
    <div className='container mx-auto'>
      <h2>{formData?.data?.name}</h2>
      <div className='flex mb-10'>
        <aside className='w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md'>
          <nav className='space-y-1 py-5'>
            {steps.map(({ name, Icon, sectionId }, index) => (
              <button
                key={sectionId}
                className={`${
                  currentStep === index ? 'bg-neutralColor-lightest text-primaryColor-normal' : 'text-neutralColor-normal'
                } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
                onClick={() => {
                  // Only allow navigation to completed steps or current step
                  if (index <= currentStep) {
                    setCurrentStep(index);
                  }
                }}
              >
                <Icon className='shrink-0 -ml-1 mr-3 h-6 w-6' />
                {name}
              </button>
            ))}
          </nav>
        </aside>
        <div className='w-full bg-gray-50'>
          <HookFormProvider defaultValues={initialValues} mode='all' resolver={yupResolver(CRValidationSchema[currentStep] as any)}>
            <HookForm onSubmit={onSubmitHandler}>
              {steps[currentStep].component}
              {isLast && (
                <div className='flex gap-4 justify-end p-6'>
                  <Button
                    label='Previous'
                    btnStyle='neutral'
                    type='button'
                    disabled={submitApplication.isLoading}
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  />
                  <Button
                    label={`${submitApplication.isLoading ? 'Submitting...' : 'Submit'}`}
                    btnStyle='primary'
                    type='submit'
                    disabled={submitApplication.isLoading}
                  />
                </div>
              )}
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
    </div>
  );
};

export const CRForm = ({ ...props }: ICRApplicationFormProps) => {
  return (
    <ApplicationFormProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} />
    </ApplicationFormProvider>
  );
};
